import { useEffect, useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import Modal from '../../../components/atoms/modal/modal';
import Button from '../../../components/atoms/button';
import { createVehicleBrand, validateBrandInput } from './functions';
import { VehicleBrand } from './types';

type CreateBrandProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (brand: VehicleBrand) => void;
  initialName?: string;
  initialWmi?: string;
};

const CreateBrand = ({ open, onClose, onCreated, initialName, initialWmi }: CreateBrandProps) => {
  const [name, setName] = useState(initialName ?? '');
  const [wmi, setWmi] = useState(initialWmi ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName ?? '');
      setWmi(initialWmi ?? '');
      setError(null);
    }
  }, [open, initialName, initialWmi]);

  const handleSave = async () => {
    const validationError = validateBrandInput(name, wmi);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      onCreated(await createVehicleBrand(name, wmi));
      onClose();
    } catch (saveError) {
      console.error('Error creating vehicle brand', saveError);
      setError('Unable to create vehicle brand.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create vehicle brand"
      footer={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim() || !wmi.trim()}>Save</Button>
        </Box>
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Brand name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          size="small"
          fullWidth
          autoFocus
        />
        <TextField
          label="WMI code"
          value={wmi}
          onChange={(event) => setWmi(event.target.value)}
          size="small"
          fullWidth
          inputProps={{ maxLength: 3 }}
        />
      </Box>
    </Modal>
  );
};

export default CreateBrand;
